// The two books — every poster, and every sticker.
//
// The activation pack is the curated day-one document: one file, a slice of
// everything, printed the week a genie goes live. These are the full range, for
// the customer three months later who wants a different sheet on the changing
// room wall and does not want to reprint sticker sheets to get it.
//
// Browser-side twins of HG-CC's `build-poster-book.mjs` and
// `build-sticker-book.mjs`. Same catalogues, same page order, same guards.
//
// Page numbering is derived once and handed to every page builder, so the
// contents table and the printed page numbers cannot drift apart.

import { contentsPage, coverPage, dividerPage, specPage, type ContentsRow } from "./frontmatter";
import {
  applicationPage,
  clingPage,
  clingPairPage,
  mixedPage,
  stickerPage,
  type PackPagePart,
} from "./physical";
import { POSTER_SECTIONS, posterCatalogue, posterPage } from "./posters";
import {
  STICKER_CATALOGUE,
  STICKER_SECTIONS,
  type StickerSection,
  type StickerSheetSpec,
} from "./stickers";
import { minQrSizeMm, printable } from "./qr";
import { brandCss, docCss, escapeHtml, font, palette } from "./tokens";
import type { PackKit } from "@/types/activation/packKit";

/**
 * A page in the sticker book that is not a grid of one sticker: the clings, the
 * matched pair, the mixed starter sheet. It carries its own renderer and the
 * same table fields a grid sheet does, so the contents and spec code does not
 * have to care which kind it is.
 */
interface BespokeSheet {
  id: string;
  render: (kit: PackKit, n: number) => Promise<PackPagePart>;
  what: string;
  detail: string;
  size: string;
  stock: string;
  use: string;
}

type BookSheet =
  | { kind: "grid"; sheet: StickerSheetSpec; page: number }
  | { kind: "custom"; sheet: BespokeSheet; page: number };

export interface BuiltDocument {
  html: string;
  pages: number;
  /** One reading per distinct code size, for the scannability panel. */
  codes: { label: string; sizeMm: number; moduleMm: number; ecc: string; modules: number }[];
  contents: ContentsRow[];
  /**
   * Sheets left out because this genie's URL cannot print on them at a
   * scannable size. Empty for almost every genie.
   */
  dropped: string[];
}

/** Wraps the assembled parts in the document shell every book and pack shares. */
function assemble(
  kit: PackKit,
  title: string,
  parts: PackPagePart[],
  contents: ContentsRow[],
  pages: number,
  dropped: string[] = [],
): BuiltDocument {
  const html = `<!doctype html><html><head><meta charset="utf-8">
    <title>${escapeHtml(title)}</title>
    <style>
      ${docCss(kit.brand)}
      ${brandCss()}
      ${parts.map((p) => p.css).join("\n")}
    </style></head><body>
    ${parts.map((p) => p.html).join("\n")}
  </body></html>`;

  // One reading per distinct printed size. Codes within a page are identical,
  // so the first on each page covers every size in the document.
  const seen = new Set<number>();
  const codes: BuiltDocument["codes"] = [];
  for (const part of parts) {
    if (!part.qr) continue;
    const key = Math.round(part.qr.sizeMm * 10);
    if (seen.has(key)) continue;
    seen.add(key);
    codes.push({
      label: part.qr.label,
      sizeMm: part.qr.sizeMm,
      moduleMm: part.qr.qr.moduleMm,
      ecc: part.qr.qr.ecc,
      modules: part.qr.qr.modules,
    });
  }
  codes.sort((a, b) => a.sizeMm - b.sizeMm);

  return { html, pages, codes, contents, dropped };
}

/** Headlines carry <br> for the layout, which has to come out for a table row. */
const plain = (s: string) => s.replace(/<br\s*\/?>/g, " ").replace(/\s+/g, " ").trim();

// ─── Poster book ─────────────────────────────────────────────────────────────
export async function buildPosterBookHtml(kit: PackKit): Promise<BuiltDocument> {
  if (!kit.url) throw new Error("Pick a genie first. Every code in the book points at its live page.");

  const all = posterCatalogue(kit);

  let n = 0;
  const P = { cover: ++n, contents: ++n };
  const sections = POSTER_SECTIONS.map((s) => {
    const posters = all.filter((p) => p.section === s.key);
    return { ...s, divider: ++n, posters: posters.map((spec) => ({ spec, page: ++n })) };
  }).filter((s) => s.posters.length > 0);
  const hangingPage = ++n;
  const specPageNo = ++n;
  const total = n;

  const rows: ContentsRow[] = sections.flatMap((s) =>
    s.posters.map(({ spec, page }) => ({
      page: String(page),
      what: plain(spec.headline || spec.quote || `${spec.stat} ${spec.statUnit}`),
      detail: spec.use,
      size: spec.layout === "a5pair" ? "Two at A5, 148 x 210mm" : "A4, 210 x 297mm",
      stock: spec.layout === "a5pair" ? "170gsm satin, cut in half" : "170gsm satin, or plain A4",
      spec: true,
    })),
  );

  const parts: PackPagePart[] = [];
  parts.push(
    await coverPage(kit, {
      eyebrow: "Poster book",
      headline: "Every poster.<br>One book.",
      lede: `${rows.length} poster designs in ${sections.length} sections, plus a page on where to hang them and a print spec for your printer. Every code points at your live genie. Print the whole book, or pull out the one page you need this week.`,
    }),
  );
  parts.push(
    contentsPage(
      {
        title: "What is in this book",
        lede: "Every page is A4 portrait, so nothing needs a tray change. Print at 100 percent with scaling off, or the codes come out smaller than they are meant to be.",
        rows,
        columns: 2,
        notes: [
          ["Print settings", 'A4, portrait, 100 percent scale, "actual size", borderless off. Colour on.'],
          ["Before you hang anything", "Print one page, scan the code from where a customer will stand, confirm it opens the genie."],
          ["Outdoors or handled", "Use a sleeve or a matte laminate. A gloss laminate reflects and the code stops scanning."],
        ],
      },
      P.contents,
    ),
  );

  for (const s of sections) {
    parts.push(dividerPage(kit, s.title, s.sub, s.divider, s.tone));
    for (const { spec, page } of s.posters) parts.push(await posterPage(kit, spec, page));
  }

  parts.push(hangingGuidePage(kit, hangingPage));

  // The spec table is condensed, not one row per poster. Every full-page design
  // is the same job on the same stock, and a printer quoting 22 identical lines
  // is a printer who stops reading at line four.
  const a5 = rows.filter((r) => r.size.startsWith("Two at A5"));
  const full = rows.filter((r) => !r.size.startsWith("Two at A5"));
  const specRows: ContentsRow[] = [
    {
      page: full.length > 1 ? `${full[0].page}-${full[full.length - 1].page}` : full[0]?.page ?? "",
      what: `Posters, ${full.length} designs, one per page`,
      detail: "",
      size: "A4, 210 x 297mm",
      stock: "170gsm satin, single sided, colour",
      spec: true,
    },
    ...a5.map((r) => ({
      page: r.page,
      what: "Half size sheet, two A5 posters",
      detail: "",
      size: "148 x 210mm, 2 up",
      stock: "170gsm satin, guillotine on the cut line",
      spec: true,
    })),
  ];

  parts.push(
    specPage(
      kit,
      {
        title: "Print spec",
        lede: "Hand this page to a print shop with the PDF and they can quote the job without a follow up email. If you are printing it yourself, the office printer settings are on page 2.",
        rows: specRows,
        extraNotes: [
          "Posters are single sided, full bleed colour to the sheet edge on the coloured designs. If the printer cannot bleed on A4, print on SRA4 and trim.",
          "The half size sheet carries two A5 posters with a cut line across the middle. Guillotine, do not fold.",
        ],
      },
      specPageNo,
    ),
  );

  return assemble(kit, `${kit.name} Poster Book`, parts, rows, total);
}

/**
 * Where to put them. A poster nobody scans is usually a poster in the wrong
 * place, not a poster with the wrong words on it.
 */
function hangingGuidePage(kit: PackKit, n: number): PackPagePart {
  const placements = (kit.placements ?? [])
    .map((p) => `<li><b>${escapeHtml(p[0])}</b><span>${escapeHtml(p[1])}</span></li>`)
    .join("");

  const css = `
    #p${n} { padding: 16mm 18mm 14mm; display: flex; flex-direction: column; }
    #p${n} .accent { position: absolute; left: 0; top: 0; width: 210mm; height: 3.5mm; background: ${palette.primary}; }
    #p${n} h1 { font-family: ${font.display}; font-size: 10.5mm; font-weight: 800; color: ${palette.ink}; margin: 0 0 2mm; }
    #p${n} .lede { font-size: 3.8mm; color: ${palette.mutedFg}; margin: 0 0 5mm; max-width: 166mm; line-height: 1.4; }
    #p${n} h2 { font-family: ${font.display}; font-size: 5.2mm; font-weight: 700; color: ${palette.ink}; margin: 0 0 2.5mm; }
    #p${n} .rules { list-style: none; margin: 0 0 5mm; padding: 0; }
    #p${n} .rules li { display: flex; gap: 5mm; align-items: flex-start; margin-bottom: 3mm; }
    #p${n} .rules .num { flex: 0 0 auto; width: 8mm; height: 8mm; border-radius: 50%;
      background: ${palette.primary}; color: #FFFFFF; font-family: ${font.display};
      font-weight: 800; font-size: 4.4mm; display: flex; align-items: center; justify-content: center; }
    #p${n} .rules b { display: block; font-size: 4mm; color: ${palette.ink}; margin-bottom: 1mm; }
    #p${n} .rules span.body { display: block; font-size: 3.4mm; color: ${palette.mutedFg}; line-height: 1.35; }
    #p${n} .where { background: ${palette.pageTint}; border: 0.3mm solid ${palette.border}; border-radius: 3mm; padding: 4mm 6mm; margin-bottom: 5mm; }
    #p${n} .where ul { list-style: none; margin: 0; padding: 0; }
    #p${n} .where li { display: flex; gap: 4mm; padding: 1.3mm 0; font-size: 3.4mm; }
    #p${n} .where li b { flex: 0 0 46mm; color: ${palette.ink}; font-weight: 600; }
    #p${n} .where li span { color: ${palette.mutedFg}; line-height: 1.3; }
    #p${n} .cols { display: flex; gap: 7mm; }
    #p${n} .cols > div { flex: 1; }
    #p${n} .cols ul { list-style: none; margin: 0; padding: 0; }
    #p${n} .cols li { font-size: 3.3mm; color: ${palette.mutedFg}; line-height: 1.35; padding: 1.2mm 0 1.2mm 5mm; position: relative; }
    #p${n} .cols li:before { content: ''; position: absolute; left: 0; top: 2.4mm; width: 2mm; height: 2mm; border-radius: 50%; background: ${palette.primary}; }
    #p${n} .cols.dont li:before { background: ${palette.secondary}; }
    #p${n} .warn { margin-top: auto; background: ${palette.pageTint}; border-left: 1.4mm solid ${palette.secondary}; border-radius: 0 3mm 3mm 0; padding: 5mm 6mm; }
    #p${n} .warn b { display: block; font-size: 3.9mm; color: ${palette.ink}; margin-bottom: 1.6mm; }
    #p${n} .warn p { margin: 0; font-size: 3.3mm; color: ${palette.mutedFg}; line-height: 1.4; }
  `;

  const html = `<div class="sheet" id="p${n}">
    <div class="accent"></div>
    <h1>Where to put them</h1>
    <p class="lede">A poster nobody scans is usually a poster in the wrong place, not a poster with
      the wrong words on it. Four rules cover almost all of it.</p>

    <ol class="rules">
      <li><span class="num">1</span><span><b>Eye height, where somebody is already standing still</b>
        <span class="body">A queue, a counter, a door somebody waits at, a lift. People scan when they have nothing else to do.</span></span></li>
      <li><span class="num">2</span><span><b>Inside the glass, facing out</b>
        <span class="body">Door and window posters go on the inside so weather cannot get at them. Check for reflections at the time of day it matters.</span></span></li>
      <li><span class="num">3</span><span><b>Match the sheet to the distance</b>
        <span class="body">Up close, any design works. Across a room, use one of the big scan targets. The code has to be readable, not just visible.</span></span></li>
      <li><span class="num">4</span><span><b>Two different sheets beat the same sheet twice</b>
        <span class="body">People stop seeing a poster they have walked past ten times. Rotate a different design in every few months, that is what the rest of this book is for.</span></span></li>
    </ol>

    <div class="where">
      <h2>Where these go at ${escapeHtml(kit.shortName || kit.name)}</h2>
      <ul>${placements}</ul>
    </div>

    <div class="cols">
      <div><h2>Do</h2><ul>
        <li>Print at 100 percent, scaling off, colour on</li>
        <li>Put the how-it-works sheet next to a busy one</li>
        <li>Use a frame or a sleeve anywhere it gets handled</li>
        <li>Scan each one after you hang it, from where a customer stands</li>
      </ul></div>
      <div class="cols dont"><h2>Do not</h2><ul>
        <li>Hang it above head height, phones cannot get square to it</li>
        <li>Put it behind glare or under a spotlight</li>
        <li>Laminate with a gloss finish, the reflection kills the code</li>
        <li>Shrink it to A5 on the printer, the code goes under the floor size</li>
      </ul></div>
    </div>

    <div class="warn">
      <b>Before you print a stack of them</b>
      <p>Print one, put it where it is going, and scan it with a phone from where a customer would
        stand. That five second check is the whole quality control process.</p>
    </div>
    <div class="pagenum">${n}</div>
  </div>`;

  return { html, css };
}

// ─── Sticker book ────────────────────────────────────────────────────────────
export async function buildStickerBookHtml(kit: PackKit): Promise<BuiltDocument> {
  if (!kit.url) throw new Error("Pick a genie first. Every code in the book points at its live page.");

  /** Pages that are not grid sheets, keyed to the section they belong in. */
  const bespoke: Partial<Record<StickerSection, BespokeSheet[]>> = {
    glass: [
      {
        id: "cling-150-90", render: clingPage,
        what: "Window clings, 150mm and 90mm", detail: "one big, one small",
        size: "150mm and 90mm circles", stock: "Static cling film or white vinyl",
        use: "Main door glass and a second window or counter front",
      },
      {
        id: "cling-120-pair", render: clingPairPage,
        what: "Window clings, 120mm", detail: "2 up, matched pair",
        size: "120mm circle, 2 up", stock: "Static cling film",
        use: "Two doors, or a door and a shopfront window",
      },
    ],
    special: [
      {
        id: "mixed-starter", render: mixedPage,
        what: "Starter sheet, mixed", detail: "squares, rounds and labels together",
        size: "75mm square, 60mm round, 90 x 35mm", stock: "White vinyl, matte laminate",
        use: "The first walk around the building, before you know which size you need",
      },
    ],
  };

  // A long genie slug pushes the smallest sheets under the printed-module floor.
  // Failing the whole book over one sheet would leave that customer with
  // nothing, so each sheet is probed first and only the ones that can carry a
  // scannable code are numbered. What was left out prints on the contents page.
  const dropped: string[] = [];
  const viable = async (item: BookSheet): Promise<boolean> => {
    const built = await printable(() =>
      item.kind === "custom" ? item.sheet.render(kit, 0) : stickerPage(kit, item.sheet, 0),
    );
    if (!built) dropped.push(item.sheet.what.toLowerCase());
    return built !== null;
  };

  let n = 0;
  const P = { cover: ++n, contents: ++n };
  const sections = [];
  for (const s of STICKER_SECTIONS) {
    const candidates: BookSheet[] = [
      ...STICKER_CATALOGUE.filter((x) => x.section === s.key).map(
        (sheet): BookSheet => ({ kind: "grid", sheet, page: 0 }),
      ),
      ...(bespoke[s.key] ?? []).map((sheet): BookSheet => ({ kind: "custom", sheet, page: 0 })),
    ];
    const pages: BookSheet[] = [];
    for (const c of candidates) if (await viable(c)) pages.push(c);
    if (pages.length === 0) continue;
    const divider = ++n;
    for (const p of pages) p.page = ++n;
    sections.push({ ...s, divider, pages });
  }
  const applicationPageNo = ++n;
  const specPageNo = ++n;
  const total = n;

  const rows: ContentsRow[] = sections.flatMap((s) =>
    s.pages.map(({ sheet, page }) => ({
      page: String(page),
      what: sheet.what,
      detail: sheet.use,
      size: sheet.size,
      stock: sheet.stock,
      spec: true,
    })),
  );

  const floor = await minQrSizeMm(kit.url);

  const parts: PackPagePart[] = [];
  parts.push(
    await coverPage(kit, {
      eyebrow: "Sticker book",
      headline: "Every sticker.<br>One book.",
      lede: `${rows.length} sheets across ${sections.length} sections: rounds, squares, labels, window clings, a mixed starter sheet and magnet artwork. Plus a page on applying them so they last, and a spec your printer can quote from. Every code points at your live genie.`,
    }),
  );
  parts.push(
    contentsPage(
      {
        title: "What is in this book",
        lede: "Every page is A4 portrait with cut guides on the trim path. Print at 100 percent with scaling off. If a sheet comes out small the codes go under the size they need to scan at, and there is no way to tell by looking.",
        rows,
        columns: 2,
        panelTitle: `Where these go at ${kit.shortName || kit.name}`,
        panelItems: kit.placements ?? [],
        notes: [
          ["Print settings", 'A4, portrait, 100 percent scale, "actual size", borderless off. Colour on.'],
          ["Stock", "White vinyl with a matte laminate for anything outdoors, on a vehicle, or handled. Paper labels are fine indoors."],
          dropped.length
            ? [
                "Not in this book",
                `${dropped.join(", ")}. Your genie's web address is ${kit.url.length} characters, so a code at those sizes prints under the ${Math.ceil(floor)}mm it needs to scan reliably.`,
              ]
            : [
                "Smallest usable code",
                `A ${Math.ceil(floor)}mm square, for this genie's URL. That is why the smallest sticker here is 50mm.`,
              ],
        ],
      },
      P.contents,
    ),
  );

  for (const s of sections) {
    parts.push(dividerPage(kit, s.title, s.sub, s.divider, s.tone));
    for (const item of s.pages) {
      parts.push(
        item.kind === "custom"
          ? await item.sheet.render(kit, item.page)
          : await stickerPage(kit, item.sheet, item.page),
      );
    }
  }

  parts.push(applicationPage(kit, applicationPageNo));
  parts.push(
    specPage(
      kit,
      {
        title: "Print spec",
        lede: "Hand this page to a print shop with the PDF and they can quote the job without a follow up email. If you are printing it yourself, the office printer settings are on page 2.",
        rows,
        extraNotes: [
          "Every sheet is multi up with the cut path drawn as a hairline dashed outline. Kiss cut the stickers and clings on that path, guillotine the magnets. Clings print on static cling film, face adhesive if they go inside glass facing out. Magnets are 0.6mm magnetic sheet, matte only, since gloss reflects under shop lighting and the code stops scanning.",
          `The smallest code in this book sits in a ${Math.ceil(floor)}mm square, which is the floor for this genie's URL at 0.65mm modules. Do not scale any sheet down.`,
        ],
      },
      specPageNo,
    ),
  );

  return assemble(kit, `${kit.name} Sticker Book`, parts, rows, total, dropped);
}
